import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLFormattedError } from 'graphql'
import { logger } from '@/utils/logging'

/**
 * Generic ApolloLink for error logging. Attempts to expose more of
 * the two types of error responses that ApolloClient will report
 * and send that data to DataDog.
 * @see https://www.apollographql.com/docs/react/data/error-handling
 */
export function logErrorLink(clientName: string) {
  return new ErrorLink(({ error, result, operation, forward }) => {
    const logContext = {
      clientName,
      context: operation.getContext(),
      operationName: operation.operationName,
      result,
      variables: operation.variables,
    }

    if (CombinedGraphQLErrors.is(error)) {
      if (handleExpectedErrors(operation.operationName, error.errors)) {
        return
      }

      logger().error('ApolloClient logErrorLink graphQLErrors', { graphQLErrors: error.errors, ...logContext })
      for (const err of error.errors) {
        if (err?.extensions?.code) {
          switch (err.extensions.code) {
            case 'INTERNAL_SERVER_ERROR':
              return forward(operation)
            // Retry failed queries for Internal Server Errors
          }
        }
      }
    } else {
      if (isExpectedNetworkError(error)) {
        return
      }
      logger().error('ApolloClient logErrorLink networkError', logContext, error)
    }
  })
}

/**
 * Determines if a network error is expected and should not be logged.
 * These are typically caused by normal user behavior, not bugs:
 * - User closes the browser/tab before request completes
 * - User navigates away from the page
 * - User loses network connectivity temporarily
 * - Mobile devices going to sleep
 */
export function isExpectedNetworkError(error: { message?: string; name?: string }): boolean {
  const message = error.message?.toLowerCase() ?? ''
  const name = error.name ?? ''

  // AbortError: Request was cancelled (user navigated away, component unmounted)
  if (name === 'AbortError') {
    return true
  }

  // Failed to fetch: Generic network failure, often caused by:
  // - User closing tab/browser mid-request
  // - Network disconnection
  // - CORS issues on cancelled requests
  if (message === 'failed to fetch') {
    return true
  }

  // Network request failed: Similar to above, used by some browsers
  if (message.includes('network request failed')) {
    return true
  }

  // Load failed: Safari's variant of network failure
  if (message === 'load failed') {
    return true
  }

  return false
}

export const handleExpectedErrors = (
  operationName: string | undefined,
  graphQLErrors: readonly GraphQLFormattedError[],
): boolean => {
  const isErrorExpected =
    (operationName === 'getProjectStory' && graphQLErrors.some((e) => e.message === 'bad_arg')) ||
    (operationName === 'getUserStory' && graphQLErrors.some((e) => e.message === 'bad_arg')) ||
    (operationName === 'getSkipCategoryPreferences' &&
      graphQLErrors.some(
        (e) => e.message === 'not_found' || e.message?.toLowerCase().includes('language not supported'),
      ))

  if (isErrorExpected) {
    // Expected errors should be handled in the query. Do not log an error or retry.
    return true
  }

  return false
}
